/**
 * File: useSearch.ts
 * Description: Hook for search functionality with debouncing and caching
 * Author: System
 * Created: 2025-01-03
 * Last Modified: 2025-01-03
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import { searchService } from '../services/searchService';
import { SearchResultDocument, SearchOptions } from '../types/search';
import { logger } from '../utils/logger';

interface UseSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  initialQuery?: string;
  autoSearch?: boolean; // Add option to disable auto-search on typing
}

interface UseSearchReturn {
  searchResults: SearchResultDocument[];
  isLoading: boolean;
  error: string | null;
  search: (query: string, options?: Partial<SearchOptions>) => void;
  clearResults: () => void;
  hasSearched: boolean;
  manualSearch: (query: string, options?: Partial<SearchOptions>) => void; // Add manual search method
}

/**
 * Hook for performing search operations with debouncing and state management
 * @param options - Configuration options for the search behavior
 * @return Search state and functions
 */
export const useSearch = (options: UseSearchOptions = {}): UseSearchReturn => {
  const {
    debounceMs = 1000, // Increase default debounce time
    minQueryLength = 2,
    initialQuery = '',
    autoSearch = false, // Disable auto-search by default
  } = options;

  const [searchResults, setSearchResults] = useState<SearchResultDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentRequestRef = useRef<AbortController | null>(null);

  /**
   * Perform the actual search operation
   * @param query - Search query string
   * @param searchOptions - Additional search options
   */
  const performSearch = useCallback(async (query: string, searchOptions: Partial<SearchOptions> = {}) => {
    // Cancel previous request if still pending
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    currentRequestRef.current = abortController;

    setIsLoading(true);
    setError(null);

    try {
      const fullOptions: SearchOptions = {
        query,
        sortBy: 'relevance',
        sortOrder: 'desc',
        limit: 50,
        ...searchOptions,
      };

      const results = await searchService.searchKnowledgeBase(fullOptions);
      
      // Only update results if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setSearchResults(results);
        setHasSearched(true);
      }
    } catch (err) {
      // Only update error if this request wasn't aborted
      if (!abortController.signal.aborted) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred during search';
        setError(errorMessage);
        // Stale results from a previous query would look like this query's results.
        setSearchResults([]);
        logger.error('Search error:', err);
      }
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        currentRequestRef.current = null;
      }
    }
  }, []);

  /**
   * Search function with debouncing (can be disabled)
   * @param query - Search query string
   * @param searchOptions - Additional search options
   */
  const search = useCallback((query: string, searchOptions: Partial<SearchOptions> = {}) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Clear results immediately if query is too short
    if (query.length < minQueryLength) {
      setSearchResults([]);
      setError(null);
      setHasSearched(false);
      return;
    }

    // If auto-search is disabled, don't perform the search
    if (!autoSearch) {
      return;
    }

    // Debounce the search
    debounceTimeoutRef.current = setTimeout(() => {
      performSearch(query, searchOptions);
    }, debounceMs);
  }, [performSearch, debounceMs, minQueryLength, autoSearch]);

  /**
   * Manual search function (bypasses debouncing and auto-search setting)
   * @param query - Search query string
   * @param searchOptions - Additional search options
   */
  const manualSearch = useCallback((query: string, searchOptions: Partial<SearchOptions> = {}) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Clear results immediately if query is too short (guideline browse is allowed empty)
    const isBrowse = searchService.isGuidelineBrowseRequest({
      query,
      filters: searchOptions.filters,
    });
    if (query.length < minQueryLength && !isBrowse) {
      setSearchResults([]);
      setError(null);
      setHasSearched(false);
      return;
    }

    // Perform search immediately without debouncing
    performSearch(query, searchOptions);
  }, [performSearch, minQueryLength]);

  /**
   * Clear search results and reset state
   */
  const clearResults = useCallback(() => {
    setSearchResults([]);
    setError(null);
    setHasSearched(false);
    setIsLoading(false);

    // Cancel pending requests
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
      currentRequestRef.current = null;
    }

    // Clear pending timeouts
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  // Perform initial search if provided
  useEffect(() => {
    if (initialQuery && initialQuery.length >= minQueryLength) {
      search(initialQuery);
    }
  }, [initialQuery, search, minQueryLength]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, []);

  return {
    searchResults,
    isLoading,
    error,
    search,
    manualSearch,
    clearResults,
    hasSearched,
  };
};
